import React, { useState } from "react";
import { Calendar, Clock, ArrowRight } from "lucide-react";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getArticles } from "../data/articles";

/* ============================
   COMPOSANTS
============================= */

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-4 py-2 rounded font-semibold transition inline-flex items-center justify-center ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-lg border border-gray-300 bg-white text-black shadow ${className}`}
  >
    {children}
  </div>
);

const Image = ({ src, alt, className = "" }) => (
  <motion.img
    src={src}
    alt={alt}
    loading="lazy"
    decoding="async"
    className={className}
    transition={{ duration: 0.25 }}
  />
);

const Badge = ({ children }) => (
  <span className="inline-flex items-center px-3 py-1 text-sm font-semibold rounded-md bg-primary/10 text-primary">
    {children}
  </span>
);

/* ============================
   PAGE BLOG
============================= */

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const { featuredPost, blogPosts } = getArticles(i18n.language);
  const [selectedPost, setSelectedPost] = useState(null);

  const openPost = (post) => {
    setSelectedPost(post);
    setTimeout(() => {
      document.getElementById("article-full")?.scrollIntoView({
        behavior: "smooth",
      });
    }, 150);
  };

  const closePost = () => {
    setSelectedPost(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background">
      <section className="py-20 bg-blue-50 mb-16 h-80">
        <div className="container max-w-[100rem] mx-auto px-6 text-center">
          <motion.h1
            className="font-heading text-6xl md:text-7xl font-black text-foreground mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {t("blog.hero.title1")}{" "}
            <span className="text-primary">{t("blog.hero.highlight")}</span>
          </motion.h1>

          <p className="font-paragraph text-xl text-foreground/80 max-w-4xl mx-auto">
            {t("blog.hero.description")}
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[100rem] mx-auto px-6">
          <h2 className="text-center text-4xl font-bold mb-10 font-heading">
            {t("blog.featured.title1")}{" "}
            <span className="text-primary">
              {" "}
              {t("blog.featured.highlight")}
            </span>
          </h2>

          <Card className="overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <Image
                src={featuredPost.image}
                alt={featuredPost.title}
                className="w-full h-full object-cover min-h-[400px]"
              />

              <div className="p-8">
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <Calendar className="w-4 h-4" />
                  {featuredPost.date}
                  <Clock className="w-4 h-4 ml-4" />
                  {featuredPost.readTime}
                </div>

                <h3 className="text-3xl font-bold mb-4">
                  {featuredPost.title}
                </h3>

                <p className="text-lg text-gray-700 mb-6 line-clamp-3">
                  {featuredPost.excerpt}
                </p>

                <Button
                  onClick={() => openPost(featuredPost)}
                  className="bg-primary text-white"
                >
                  {t("blog.featured.readMore")}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section className="py-20 bg-blue-100">
        <div className="max-w-[100rem] mx-auto px-6 grid md:grid-cols-2 gap-8">
          {blogPosts.map((post) => (
            <Card key={post.id} className="overflow-hidden flex flex-col">
              <Image
                src={post.image}
                alt={post.title}
                className="w-full h-56 object-cover"
              />

              <div className="p-6 flex flex-col h-full">
                <div className="text-sm text-gray-500 mb-2">
                  <Calendar className="inline w-4 h-4 mr-1" />
                  {post.date}
                </div>

                <h3 className="text-xl font-bold mb-3">{post.title}</h3>

                <p className="text-gray-700 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                <Button
                  onClick={() => openPost(post)}
                  className="mt-auto bg-primary text-white"
                >
                  {t("blog.posts.readArticle")}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <div id="article-full" className="max-w-4xl mx-auto px-6 mb-20 mt-20">
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-lg shadow-lg"
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-3xl font-bold">{selectedPost.title}</h2>

              <button
                onClick={closePost}
                className="bg-red-500 text-white px-3 py-1 rounded"
              >
                ✕
              </button>
            </div>

            <div className="relative w-full h-90 overflow-hidden rounded mb-6">
              <Image
                src={selectedPost.image}
                alt={selectedPost.title}
                className="w-full h-full object-cover scale-105 object-center"
              />
            </div>

            <p className="text-lg whitespace-pre-line leading-relaxed">
              {selectedPost.content}
            </p>

            {selectedPost.gallery && (
              <div className="mt-10">
                <h3 className="text-2xl font-bold mb-4"> {t("blog.article.gallery")}</h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  {selectedPost.gallery.map((img, i) => (
                    <Image
                      key={i}
                      src={img}
                      alt={`Galerie ${i + 1}`}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              {selectedPost.tags.map((tag, i) => (
                <Badge key={i}>{tag}</Badge>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      <section className="py-20 bg-gradient-to-r from-secondary to-primary text-white">
        <div className="text-center max-w-3xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-6">
            {t("blog.cta.title")}
          </h2>

          <p className="text-xl mb-8">
           
  {t("blog.cta.description")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-primary">
              <Link to="/contact">   {t("blog.cta.shareStory")}</Link>
            </Button>

            <Button className="bg-white text-primary">
              <Link to="/members">{t("blog.cta.joinCommunity")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
